"use client";

import { Button, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { Icon } from "@repo/dashboard-core";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState, useTransition } from "react";

import { recoverPassword } from "@repo/dashboard-core/actions";
import {
  recoverSchema,
  type RecoverFormValues,
} from "@repo/dashboard-core/schemas";

export default function RecoverPage() {
  const t = useTranslations("auth.recover");
  const tv = useTranslations("validation");
  const params = useParams<{ locale: string }>();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm<RecoverFormValues>({
    initialValues: { email: "" },
    validate: zodResolver(recoverSchema((key) => tv(key))),
  });

  const handleSubmit = (values: RecoverFormValues) => {
    startTransition(async () => {
      const result = await recoverPassword({
        email: values.email,
        origin: window.location.origin,
      });

      if (!result.success) {
        notifications.show({
          title: t("error-title"),
          message: t("error-message"),
          color: "red",
        });
        return;
      }

      setEmailSent(true);
      setTimeout(() => {
        router.replace(`/${params.locale}/login`);
      }, 3000);
    });
  };

  if (emailSent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-light">
          <span className="text-primary">
            <Icon name="email" size={28} />
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-primary">
            {t("success-title")}
          </h1>
          <p className="mt-2 text-base text-text-secondary">
            {t("success-message")}
          </p>
        </div>
        <Link
          href={`/${params.locale}/login`}
          className="inline-block text-base font-medium text-primary hover:underline"
        >
          {t("back-to-login")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          {t("title")}
        </h1>
        <p className="mt-2 text-base text-text-secondary">{t("subtitle")}</p>
      </div>

      <form className="space-y-5" onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label={t("email")}
          placeholder="you@example.com"
          type="email"
          size="md"
          radius="lg"
          {...form.getInputProps("email")}
        />

        <Button
          type="submit"
          fullWidth
          size="md"
          radius="lg"
          loading={isPending}
        >
          {t("submit")}
        </Button>
      </form>

      <div className="text-center">
        <Link
          href={`/${params.locale}/login`}
          className="text-base text-primary hover:underline"
        >
          {t("back-to-login")}
        </Link>
      </div>
    </div>
  );
}
